import { useState, useEffect, useRef } from 'react';
import { API_BASE_URL, convertImageUrl } from './config';

interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: 'host' | 'admin';
}

interface InquiryReply {
  id: number;
  user_id: string;
  user_name: string;
  user_role: string;
  content: string;
  images: string[];
  created_at: string;
}

interface Inquiry {
  id: string;
  user_id: string;
  user_name: string;
  user_role: string;
  title: string;
  category: string;
  content: string;
  images: string[];
  status: string;
  reply_count: number;
  created_at: string;
  updated_at: string;
  replies?: InquiryReply[];
}

interface Props {
  token: string;
  user: AuthUser | null;
}

const CATEGORIES = ['질문', '기능요청', '버그신고', '기타'] as const;
const STATUSES = ['답변대기', '답변완료', '해결완료'] as const;

const categoryColors: Record<string, { bg: string; color: string }> = {
  '질문':     { bg: '#eff6ff', color: '#2563eb' },
  '기능요청': { bg: '#f5f3ff', color: '#7c3aed' },
  '버그신고': { bg: '#fef2f2', color: '#dc2626' },
  '기타':     { bg: '#f3f4f6', color: '#6b7280' },
};

const statusColors: Record<string, { bg: string; color: string }> = {
  '답변대기': { bg: '#fff7ed', color: '#ea580c' },
  '답변완료': { bg: '#eff6ff', color: '#2563eb' },
  '해결완료': { bg: '#f0fdf4', color: '#16a34a' },
};

function formatDate(iso: string): string {
  if (!iso) return '';
  const utc = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
  const d = new Date(utc);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return '방금 전';
  if (mins < 60) return `${mins}분 전`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}시간 전`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}일 전`;
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

function formatFullDate(iso: string): string {
  if (!iso) return '';
  const utc = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
  const d = new Date(utc);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export default function InquiryBoard({ token, user }: Props) {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [selected, setSelected] = useState<Inquiry | null>(null);
  const [loading, setLoading] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>('전체');
  const [filterStatus, setFilterStatus] = useState<string>('전체');

  // Write modal
  const [showWrite, setShowWrite] = useState(false);
  const [writeCategory, setWriteCategory] = useState<string>('질문');
  const [writeTitle, setWriteTitle] = useState('');
  const [writeContent, setWriteContent] = useState('');
  const [writeSubmitting, setWriteSubmitting] = useState(false);

  // Reply
  const [replyContent, setReplyContent] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);

  // Images
  const [writeImages, setWriteImages] = useState<string[]>([]);
  const [writeUploading, setWriteUploading] = useState(false);
  const [replyImages, setReplyImages] = useState<string[]>([]);
  const [replyUploading, setReplyUploading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const writeFileRef = useRef<HTMLInputElement>(null);
  const replyFileRef = useRef<HTMLInputElement>(null);

  // Toast
  const [toast, setToast] = useState('');

  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  // Image upload
  const uploadImages = async (
    files: FileList | File[],
    current: string[],
    setCurrent: React.Dispatch<React.SetStateAction<string[]>>,
    setUploading: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    const remaining = 5 - current.length;
    if (remaining <= 0) { showToast('이미지는 최대 5개까지 첨부 가능합니다'); return; }
    const toUpload = Array.from(files).slice(0, remaining);
    setUploading(true);
    for (const file of toUpload) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await fetch(`${API_BASE_URL}/api/upload-image`, { method: 'POST', body: formData });
        if (res.ok) {
          const data = await res.json();
          setCurrent(prev => [...prev, data.url]);
        }
      } catch { /* ignore */ }
    }
    setUploading(false);
  };

  // Paste handler for images
  const handlePaste = (
    e: React.ClipboardEvent,
    current: string[],
    setCurrent: React.Dispatch<React.SetStateAction<string[]>>,
    setUploading: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) imageFiles.push(file);
      }
    }
    if (imageFiles.length > 0) {
      e.preventDefault();
      uploadImages(imageFiles, current, setCurrent, setUploading);
    }
  };

  // Fetch list
  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/inquiries`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setInquiries(data);
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  // Fetch detail
  const fetchDetail = async (id: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/inquiries/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setSelected(data);
      }
    } catch { /* ignore */ }
  };

  useEffect(() => { if (token) fetchList(); }, [token]);

  // Create
  const handleCreate = async () => {
    if (!writeTitle.trim() || !writeContent.trim()) return;
    setWriteSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/inquiries`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ title: writeTitle, category: writeCategory, content: writeContent, images: writeImages }),
      });
      if (res.ok) {
        setShowWrite(false);
        setWriteTitle(''); setWriteContent(''); setWriteCategory('질문'); setWriteImages([]);
        showToast('문의가 등록되었습니다');
        fetchList();
      }
    } catch { /* ignore */ }
    setWriteSubmitting(false);
  };

  // Reply
  const handleReply = async () => {
    if (!selected || !replyContent.trim()) return;
    setReplySubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/inquiries/${selected.id}/replies`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ content: replyContent, images: replyImages }),
      });
      if (res.ok) {
        setReplyContent(''); setReplyImages([]);
        fetchDetail(selected.id);
        fetchList();
      }
    } catch { /* ignore */ }
    setReplySubmitting(false);
  };

  // Status change
  const handleStatusChange = async (id: string, status: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/inquiries/${id}/status`, {
        method: 'PUT', headers: authHeaders,
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        showToast(`상태가 '${status}'(으)로 변경되었습니다`);
        fetchDetail(id);
        fetchList();
      }
    } catch { /* ignore */ }
  };

  // Delete
  const handleDelete = async (id: string) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/inquiries/${id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        showToast('삭제되었습니다');
        setSelected(null);
        fetchList();
      }
    } catch { /* ignore */ }
  };

  // Filter
  const filtered = inquiries.filter(inq => {
    if (filterCategory !== '전체' && inq.category !== filterCategory) return false;
    if (filterStatus !== '전체' && inq.status !== filterStatus) return false;
    return true;
  });

  const isAdmin = user?.role === 'admin';

  // ─── Badge component ───
  const Badge = ({ label, colors }: { label: string; colors: { bg: string; color: string } }) => (
    <span style={{
      display: 'inline-block', fontSize: 11, fontWeight: 600,
      padding: '2px 8px', borderRadius: 4,
      backgroundColor: colors.bg, color: colors.color,
    }}>{label}</span>
  );

  // ─── ImageGrid: 첨부 이미지 표시 ───
  const ImageGrid = ({ images }: { images?: string[] }) => {
    if (!images || images.length === 0) return null;
    return (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        {images.map((url, i) => (
          <img
            key={i}
            src={convertImageUrl(url)}
            alt=""
            onClick={() => setLightboxUrl(convertImageUrl(url))}
            style={{
              width: 120, height: 90, objectFit: 'cover', borderRadius: 8,
              cursor: 'pointer', border: '1px solid #e2e8f0',
            }}
          />
        ))}
      </div>
    );
  };

  // ─── ImagePicker: 이미지 선택/프리뷰 ───
  const ImagePicker = ({ images, setImages, uploading, fileRef }: {
    images: string[];
    setImages: React.Dispatch<React.SetStateAction<string[]>>;
    uploading: boolean;
    fileRef: React.RefObject<HTMLInputElement | null>;
  }) => (
    <div style={{ marginTop: 10 }}>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={e => {
          if (e.target.files && e.target.files.length > 0) {
            const setUploading = setImages === setWriteImages ? setWriteUploading : setReplyUploading;
            uploadImages(e.target.files, images, setImages, setUploading);
            e.target.value = '';
          }
        }}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading || images.length >= 5}
        style={{
          padding: '5px 12px', fontSize: 12, fontWeight: 500, borderRadius: 6,
          border: '1px solid #d1d5db', background: '#f8fafc', color: '#475569',
          cursor: uploading || images.length >= 5 ? 'not-allowed' : 'pointer',
          opacity: uploading ? 0.6 : 1,
        }}
      >
        {uploading ? '업로드 중...' : `이미지 첨부 (${images.length}/5)`}
      </button>
      {images.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          {images.map((url, i) => (
            <div key={i} style={{ position: 'relative' }}>
              <img
                src={convertImageUrl(url)}
                alt=""
                style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }}
              />
              <button
                type="button"
                onClick={() => setImages(prev => prev.filter((_, idx) => idx !== i))}
                style={{
                  position: 'absolute', top: -6, right: -6, width: 18, height: 18,
                  borderRadius: '50%', border: 'none', background: '#ef4444', color: '#fff',
                  fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  lineHeight: 1, padding: 0,
                }}
              >×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ─── DETAIL VIEW ───
  if (selected) {
    return (
      <div style={{ padding: '24px 0', maxWidth: 720, margin: '0 auto' }}>
        {/* Back */}
        <button
          onClick={() => { setSelected(null); setReplyContent(''); }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 14, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4, padding: 0, marginBottom: 20,
          }}
        >
          <span style={{ fontSize: 18 }}>&larr;</span> 목록으로
        </button>

        {/* Inquiry card */}
        <div style={{
          background: '#fff', borderRadius: 12, border: '1px solid #e2e5e9',
          padding: '24px 28px', marginBottom: 24,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Badge label={selected.category} colors={categoryColors[selected.category] || categoryColors['기타']} />
            <Badge label={selected.status} colors={statusColors[selected.status] || statusColors['답변대기']} />
          </div>
          <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 700, color: '#1a1a1a' }}>{selected.title}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>
            <span>{selected.user_name}</span>
            <span>{formatFullDate(selected.created_at)}</span>
          </div>
          <div style={{ fontSize: 15, lineHeight: 1.7, color: '#334155', whiteSpace: 'pre-wrap' }}>
            {selected.content}
          </div>
          <ImageGrid images={selected.images} />

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, marginTop: 24, borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
            {/* host: 해결완료 버튼 */}
            {!isAdmin && selected.status === '답변완료' && selected.user_id === user?.id && (
              <button
                onClick={() => handleStatusChange(selected.id, '해결완료')}
                style={{
                  padding: '6px 14px', fontSize: 13, fontWeight: 500, borderRadius: 6,
                  border: '1px solid #16a34a', background: '#f0fdf4', color: '#16a34a', cursor: 'pointer',
                }}
              >
                해결완료
              </button>
            )}
            {/* admin: status dropdown */}
            {isAdmin && (
              <select
                value={selected.status}
                onChange={e => handleStatusChange(selected.id, e.target.value)}
                style={{
                  padding: '6px 12px', fontSize: 13, borderRadius: 6,
                  border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer',
                }}
              >
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
            {/* delete */}
            {(isAdmin || selected.user_id === user?.id) && (
              <button
                onClick={() => handleDelete(selected.id)}
                style={{
                  padding: '6px 14px', fontSize: 13, fontWeight: 500, borderRadius: 6,
                  border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', cursor: 'pointer',
                  marginLeft: 'auto',
                }}
              >
                삭제
              </button>
            )}
          </div>
        </div>

        {/* Replies */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#475569', marginBottom: 16 }}>
            답변 {selected.replies?.length || 0}개
          </h3>
          {selected.replies?.map(r => {
            const isAdminReply = r.user_role === 'admin';
            return (
              <div key={r.id} style={{
                marginBottom: 12, padding: '16px 20px', borderRadius: 10,
                background: isAdminReply ? '#f0f7ff' : '#fff',
                border: isAdminReply ? 'none' : '1px solid #e2e5e9',
                borderLeft: isAdminReply ? '3px solid #1976d2' : '1px solid #e2e5e9',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  {isAdminReply ? (
                    <span style={{
                      fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 3,
                      backgroundColor: '#1976d2', color: '#fff',
                    }}>관리자</span>
                  ) : (
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
                      {r.user_name}
                    </span>
                  )}
                  <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>
                    {formatFullDate(r.created_at)}
                  </span>
                </div>
                <div style={{ fontSize: 14, lineHeight: 1.7, color: '#334155', whiteSpace: 'pre-wrap' }}>
                  {r.content}
                </div>
                <ImageGrid images={r.images} />
              </div>
            );
          })}
        </div>

        {/* Reply input */}
        <div style={{
          background: '#fff', borderRadius: 12, border: '1px solid #e2e5e9', padding: '16px 20px',
        }}>
          <textarea
            value={replyContent}
            onChange={e => setReplyContent(e.target.value)}
            onPaste={e => handlePaste(e, replyImages, setReplyImages, setReplyUploading)}
            placeholder="답변을 입력하세요... (이미지 붙여넣기 가능)"
            rows={3}
            style={{
              width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px',
              fontSize: 14, resize: 'vertical', outline: 'none', boxSizing: 'border-box',
              fontFamily: 'inherit',
            }}
            onFocus={e => { e.currentTarget.style.borderColor = '#2563eb'; }}
            onBlur={e => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
          />
          <ImagePicker images={replyImages} setImages={setReplyImages} uploading={replyUploading} fileRef={replyFileRef} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
            <button
              onClick={handleReply}
              disabled={replySubmitting || !replyContent.trim()}
              style={{
                padding: '8px 20px', fontSize: 14, fontWeight: 600, borderRadius: 8,
                border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer',
                opacity: replySubmitting || !replyContent.trim() ? 0.5 : 1,
              }}
            >
              {replySubmitting ? '등록 중...' : '답변 등록'}
            </button>
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div style={{
            position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)',
            background: '#1e293b', color: '#fff', padding: '10px 24px', borderRadius: 8,
            fontSize: 14, zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}>{toast}</div>
        )}

        {/* Lightbox */}
        {lightboxUrl && (
          <div
            onClick={() => setLightboxUrl(null)}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.85)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', zIndex: 20000,
              cursor: 'pointer',
            }}
          >
            <img src={lightboxUrl} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8 }} />
          </div>
        )}
      </div>
    );
  }

  // ─── LIST VIEW ───
  return (
    <div style={{ padding: '24px 0', maxWidth: 720, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a1a' }}>문의/요청하기</h2>
        <button
          onClick={() => setShowWrite(true)}
          style={{
            padding: '8px 20px', fontSize: 14, fontWeight: 600, borderRadius: 8,
            border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer',
          }}
        >
          글쓰기
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        {/* Category */}
        {['전체', ...CATEGORIES].map(c => (
          <button key={c} onClick={() => setFilterCategory(c)} style={{
            padding: '5px 12px', fontSize: 12, fontWeight: filterCategory === c ? 600 : 400,
            borderRadius: 16, border: filterCategory === c ? '1px solid #2563eb' : '1px solid #d1d5db',
            background: filterCategory === c ? '#eff6ff' : '#fff',
            color: filterCategory === c ? '#2563eb' : '#64748b',
            cursor: 'pointer',
          }}>{c}</button>
        ))}
        <span style={{ width: 1, height: 20, background: '#e2e8f0', margin: '0 4px', alignSelf: 'center' }} />
        {/* Status */}
        {['전체', ...STATUSES].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)} style={{
            padding: '5px 12px', fontSize: 12, fontWeight: filterStatus === s ? 600 : 400,
            borderRadius: 16, border: filterStatus === s ? '1px solid #2563eb' : '1px solid #d1d5db',
            background: filterStatus === s ? '#eff6ff' : '#fff',
            color: filterStatus === s ? '#2563eb' : '#64748b',
            cursor: 'pointer',
          }}>{s}</button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>불러오는 중...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 14 }}>
          등록된 문의가 없습니다
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(inq => (
            <div
              key={inq.id}
              onClick={() => fetchDetail(inq.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '14px 18px', background: '#fff', borderRadius: 10,
                border: '1px solid #e2e5e9', cursor: 'pointer',
                transition: 'border-color 0.15s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#94a3b8'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#e2e5e9'; }}
            >
              <Badge label={inq.category} colors={categoryColors[inq.category] || categoryColors['기타']} />
              <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {inq.title}
              </span>
              {inq.reply_count > 0 && (
                <span style={{ fontSize: 12, color: '#2563eb', fontWeight: 600 }}>
                  답변 {inq.reply_count}
                </span>
              )}
              {isAdmin && (
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{inq.user_name}</span>
              )}
              <Badge label={inq.status} colors={statusColors[inq.status] || statusColors['답변대기']} />
              <span style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{formatDate(inq.created_at)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Write Modal */}
      {showWrite && (
        <div
          onMouseDown={e => { if (e.target === e.currentTarget) setShowWrite(false); }}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000,
          }}
        >
          <div style={{
            background: '#fff', borderRadius: 16, width: 480, maxHeight: '90vh',
            overflow: 'auto', padding: '28px 32px',
          }}>
            <h3 style={{ margin: '0 0 24px', fontSize: 18, fontWeight: 700 }}>문의 작성</h3>

            {/* Category selection */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>카테고리</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {CATEGORIES.map(c => (
                  <button key={c} onClick={() => setWriteCategory(c)} style={{
                    padding: '6px 14px', fontSize: 13, fontWeight: writeCategory === c ? 600 : 400,
                    borderRadius: 8,
                    border: writeCategory === c ? `2px solid ${categoryColors[c].color}` : '1px solid #d1d5db',
                    background: writeCategory === c ? categoryColors[c].bg : '#fff',
                    color: writeCategory === c ? categoryColors[c].color : '#64748b',
                    cursor: 'pointer',
                  }}>{c}</button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>제목</label>
              <input
                value={writeTitle}
                onChange={e => setWriteTitle(e.target.value)}
                placeholder="문의 제목을 입력하세요"
                style={{
                  width: '100%', padding: '10px 14px', fontSize: 14, border: '1px solid #d1d5db',
                  borderRadius: 8, outline: 'none', boxSizing: 'border-box',
                }}
                onFocus={e => { e.currentTarget.style.borderColor = '#2563eb'; }}
                onBlur={e => { e.currentTarget.style.borderColor = '#d1d5db'; }}
              />
            </div>

            {/* Content */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>내용</label>
              <textarea
                value={writeContent}
                onChange={e => setWriteContent(e.target.value)}
                onPaste={e => handlePaste(e, writeImages, setWriteImages, setWriteUploading)}
                placeholder="문의 내용을 자세히 작성해주세요 (이미지 붙여넣기 가능)"
                rows={6}
                style={{
                  width: '100%', padding: '10px 14px', fontSize: 14, border: '1px solid #d1d5db',
                  borderRadius: 8, outline: 'none', resize: 'vertical', boxSizing: 'border-box',
                  fontFamily: 'inherit',
                }}
                onFocus={e => { e.currentTarget.style.borderColor = '#2563eb'; }}
                onBlur={e => { e.currentTarget.style.borderColor = '#d1d5db'; }}
              />
              <ImagePicker images={writeImages} setImages={setWriteImages} uploading={writeUploading} fileRef={writeFileRef} />
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => { setShowWrite(false); setWriteTitle(''); setWriteContent(''); setWriteCategory('질문'); setWriteImages([]); }}
                style={{
                  padding: '9px 20px', fontSize: 14, fontWeight: 500, borderRadius: 8,
                  border: '1px solid #d1d5db', background: '#fff', color: '#475569', cursor: 'pointer',
                }}
              >
                취소
              </button>
              <button
                onClick={handleCreate}
                disabled={writeSubmitting || !writeTitle.trim() || !writeContent.trim()}
                style={{
                  padding: '9px 24px', fontSize: 14, fontWeight: 600, borderRadius: 8,
                  border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer',
                  opacity: writeSubmitting || !writeTitle.trim() || !writeContent.trim() ? 0.5 : 1,
                }}
              >
                {writeSubmitting ? '등록 중...' : '등록'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)',
          background: '#1e293b', color: '#fff', padding: '10px 24px', borderRadius: 8,
          fontSize: 14, zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>{toast}</div>
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 20000,
            cursor: 'pointer',
          }}
        >
          <img src={lightboxUrl} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8 }} />
        </div>
      )}
    </div>
  );
}
